import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  Milestone,
  MilestoneConnections,
  MilestoneGroup,
  MilestoneItem,
  MilestoneItemName,
  MilestoneStage,
  MilestoneTask,
  MilestoneTaskConnection,
  ResponseState,
  StageName,
  StudyMilestones,
  StudyMilestonesLabel
} from '@models';
import {
  CommonFunctionsService,
  GroupTooltipService,
  MilestoneTooltipService,
  StageTooltipService,
  StudyGanttChartCalculationService,
  StyleUtilService
} from '@services';
import {
  GanttChartComponent,
  GanttChartTaskColumn,
  GanttChartTooltip
} from 'smart-webcomponents-angular/ganttchart';

@Component({
  selector: 'study-gantt-chart',
  templateUrl: './study-gantt-chart.component.html',
  styleUrl: './study-gantt-chart.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class StudyGanttChartComponent implements OnInit, AfterViewInit {
  private milestoneTooltipService: MilestoneTooltipService = inject(MilestoneTooltipService);
  private stageTooltipService: StageTooltipService = inject(StageTooltipService);
  private groupTooltipService: GroupTooltipService = inject(GroupTooltipService);
  private studyGanttChartCalculationService: StudyGanttChartCalculationService = inject(
    StudyGanttChartCalculationService
  );
  private commonFunctionsService: CommonFunctionsService = inject(CommonFunctionsService);
  private styleUtilService: StyleUtilService = inject(StyleUtilService);

  private milestoneConnectionsMap = new Map<MilestoneItemName, MilestoneConnections>();
  private scrollDate!: Date | undefined;
  private currentExpandedStages: StageName[] = [];
  private dataSourceObjId = -1;
  @Input() studyMilestonesRes!: ResponseState<StudyMilestones>;
  @Output() expandedStagesChange = new EventEmitter<StageName[]>();

  @ViewChild('ganttchart', { read: GanttChartComponent, static: false })
  ganttchart!: GanttChartComponent;

  dataSource: any[] = [];
  dataLoaded = false;
  taskColumns: GanttChartTaskColumn[] = [];
  tooltip: GanttChartTooltip = { enabled: true, delay: 300 };
  currentTimeIndicator = true;
  taskTreeMinDefaultSize = 240;

  showErrorBlock = false;

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.ganttchart && this.scrollDate) {
        this.ganttchart.refresh();
        this.ganttchart.scrollToDate(this.scrollDate);
        this.disableConnectionEdgeTooltip();
      }
    }, 0);
  }

  ngOnInit() {
    if (this.studyMilestonesRes.status === 'success') {
      const studyMilestones = this.studyMilestonesRes.data;
      this.transformStudyMilestonesToDataSource(studyMilestones);
      const componentReference = this;

      this.taskColumns = [
        {
          label: 'Stages and milestones',
          value: 'label',
          formatFunction: function (label: StudyMilestonesLabel) {
            return componentReference.studyGanttChartCalculationService.getTreeviewFormattedLabel(
              label,
              studyMilestones
            );
          }
        }
      ];

      this.expandedStagesChange.emit(this.currentExpandedStages);
      this.dataLoaded = true;
    } else {
      this.showErrorBlock = true;
    }
  }

  disableConnectionTooltip($event: CustomEvent) {
    this.styleUtilService.modifyGanttTooltipBadge();

    if ($event.detail.type === 'connection') {
      $event.preventDefault();
    }
  }

  disableElementConnections(event: CustomEvent) {
    event.preventDefault();
  }

  handleStageToggle({ type, detail }: CustomEvent) {
    const label = detail.item.label as StageName;
    if (this.studyGanttChartCalculationService.isGroupLabel(label)) return;

    this.currentExpandedStages =
      type === 'expand'
        ? [...this.currentExpandedStages, label]
        : this.currentExpandedStages.filter((stage) => stage !== label);

    this.expandedStagesChange.emit(this.currentExpandedStages);
  }

  private transformStudyMilestonesToDataSource(studyMilestones: StudyMilestones): void {
    const studyHasAllCompletedStages =
      this.studyGanttChartCalculationService.studyHasAllCompletedStages(studyMilestones);
    this.dataSource = studyMilestones.stages.map((stage: MilestoneStage) => {
      if (stage.status === 'In Progress') {
        this.scrollDate =
          this.studyGanttChartCalculationService.calculateScrollDateFromInProgressStage(stage);
      }

      const stageIsExpanded = this.studyGanttChartCalculationService.stageIsExpanded(
        studyHasAllCompletedStages,
        stage
      );

      if (stageIsExpanded) this.currentExpandedStages.push(stage.stage);
      const stageTask = this.createStageTask(stage, stageIsExpanded);
      return stageTask;
    });

    this.configureMilestoneConnections(studyMilestones.stages);
    this.assignConnectionsToTasks();
    if (studyHasAllCompletedStages) {
      this.scrollDate =
        this.studyGanttChartCalculationService.calculateScrollDateFromStudy(studyMilestones);
    }
  }

  private createStageTask(stage: MilestoneStage, stageIsExpanded: boolean) {
    this.dataSourceObjId++;

    return {
      id: this.dataSourceObjId,
      label: stage.stage,
      expanded: stageIsExpanded,
      dateStart: stage.startDates.date,
      dateEnd: stage.endDates.date,
      type: 'project',
      tooltip: this.stageTooltipService.buildHTML(stage),
      tasks: this.getTasks(stage),
      connections: [] as MilestoneTaskConnection[],
      class:
        'stage_' + (stage.status ? stage.status.replace(' ', '_').toLocaleLowerCase() : 'upcoming')
    };
  }

  private getTasks(stage: MilestoneStage): any[] {
    const tasks: any[] = [];
    stage.milestones.forEach((milestoneItem) => {
      if (milestoneItem.type == 'group') {
        tasks.push(this.createGroupTask(milestoneItem));
      } else if (milestoneItem.type == 'milestone') {
        const milestoneTask = this.createMilestoneTask(milestoneItem);
        if (milestoneTask) tasks.push(milestoneTask);
      }
    });

    return tasks;
  }

  private createGroupTask(group: MilestoneGroup) {
    this.dataSourceObjId++;
    this.populateMilestoneConnectionMap(group, this.dataSourceObjId);

    return {
      id: this.dataSourceObjId,
      label: group.name,
      type: 'project',
      dateStart: group.startDates.date,
      dateEnd: group.endDates.date,
      tooltip: this.groupTooltipService.buildHTML(group),
      expanded: true,
      tasks: group.milestones.flatMap((milestone) => {
        const milestoneTask = this.createMilestoneTask(milestone);
        return milestoneTask ? [milestoneTask] : [];
      }),
      connections: [] as MilestoneTaskConnection[],
      class:
        'group_' + (group.status ? group.status.replace(' ', '_').toLocaleLowerCase() : 'upcoming')
    };
  }

  private createMilestoneTask(milestone: Milestone) {
    const endDate = this.studyGanttChartCalculationService.getMilestoneEndDate(milestone.values);
    if (!endDate) return null;

    let styleClass = 'milestone_';
    if (milestone.status.includes('Completed')) {
      styleClass += 'completed';
    } else {
      styleClass += milestone.status.replace(/[ |]+/g, '_').toLowerCase();
    }
    this.dataSourceObjId++;

    this.populateMilestoneConnectionMap(milestone, this.dataSourceObjId);

    return {
      id: this.dataSourceObjId,
      label: milestone.name,
      dateEnd: endDate,
      type: 'milestone',
      class: styleClass,
      tooltip: this.milestoneTooltipService.buildHTML(milestone),
      connections: [] as MilestoneTaskConnection[]
    };
  }

  private populateMilestoneConnectionMap(milestoneItem: MilestoneItem, id: number) {
    this.milestoneConnectionsMap.set(milestoneItem.name, {
      id,
      connections: milestoneItem.type == 'group' ? [] : (milestoneItem.connections ?? []),
      connectionIds: [] // Is populated once all of the IDs are assigned
    });
  }

  private configureMilestoneConnections(stages: MilestoneStage[]) {
    const allMilestones = this.commonFunctionsService.getMilestonesFlatMapFromStages(stages);
    for (const milestone of allMilestones) {
      const milestoneConnection = this.milestoneConnectionsMap.get(milestone.name);
      if (!milestoneConnection) continue;

      for (const connection of milestoneConnection.connections) {
        const connectionId = this.milestoneConnectionsMap.get(connection)?.id;
        if (connectionId !== undefined) {
          milestoneConnection.connectionIds.push(connectionId);
        }
      }
    }
  }

  private assignConnectionsToTasks() {
    this.dataSource
      .flatMap((stageTask) => stageTask.tasks)
      .flatMap((task: MilestoneTask) => [task, ...(task.tasks ?? [])])
      .forEach((task) => this.addTaskConnections(task));
  }

  private addTaskConnections(task: MilestoneTask) {
    const milestoneConnection = this.milestoneConnectionsMap.get(task.label);
    if (milestoneConnection && milestoneConnection.connectionIds.length > 0) {
      task.connections = milestoneConnection.connectionIds.map((connectionLocationIndex) => ({
        target: connectionLocationIndex,
        type: 1
      }));
    }
  }

  private disableConnectionEdgeTooltip() {
    this.ganttchart.tooltipFormatFunction = (
      _item: any,
      event: { target: { classList: { contains: (arg0: string) => any } } }
    ) => {
      if (event.target.classList.contains('smart-task-connection')) {
        return '';
      }
      return undefined;
    };
  }
}
